/**
 * Deterministic projection of a seller's geo-position onto the
 * `MapPlaceholder` 0–100% pin coordinate space (design revamp).
 *
 * `MapPlaceholder` is a faux 2GIS illustration whose pins are positioned by
 * percentage (`{ x, y }`, 0–100). Real seller lat/lng (Decimal-as-string from
 * the API) are linearly mapped from the Bishkek bounding box onto that box, so
 * pins land in plausible relative positions and the map stays stable across
 * renders. The real 2GIS embed (`features/marketplace/SellerMap.tsx`) replaces
 * this when it lands; this is the placeholder seam.
 *
 * Sellers missing lat/lng (nullable server-side) are laid out on a stable grid
 * keyed by a hash of the seller id, so every seller still gets a deterministic
 * pin (no overlap stacking at 0,0, no jitter between renders).
 */
import { parseDecimal } from "@/lib/format";
import type { MapPin } from "@garden/ui";
import type { SellerSummary } from "@/lib/garden-api";

/**
 * Bishkek bounding box (approx). Sellers within this range map linearly onto
 * the 0–100% pin space; the placeholder map is not geographically precise, so
 * a generous box that covers the city's seller markets is sufficient.
 *   lat ~ 42.79 (south) → 42.92 (north)
 *   lng ~ 74.52 (west)  → 74.68 (east)
 */
const BISHKEK_BOUNDS = {
  latMin: 42.79,
  latMax: 42.92,
  lngMin: 74.52,
  lngMax: 74.68,
} as const;

/** Inset (in %) so projected pins never hug the very edge of the map box. */
const EDGE_INSET = 8;

/** A pin carrying the seller it represents, forwarded to MapPlaceholder.onPin. */
export interface SellerMapPin extends MapPin {
  seller: SellerSummary;
}

/** Clamp to the inset band [EDGE_INSET, 100 - EDGE_INSET]. */
function clampPct(value: number): number {
  const lo = EDGE_INSET;
  const hi = 100 - EDGE_INSET;
  if (value < lo) return lo;
  if (value > hi) return hi;
  return value;
}

/** Stable 32-bit string hash (FNV-1a) — deterministic across renders/sessions. */
function hashString(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    // 32-bit FNV prime multiply, kept in the unsigned range.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * Lay a coordinate-less seller onto a stable grid derived from its id hash.
 * A 5×5 grid (cells inset from the edges) spreads the seven demo sellers
 * without overlap; the hash picks a cell deterministically.
 */
function hashedGridPosition(sellerId: string): { x: number; y: number } {
  const hash = hashString(sellerId);
  const cols = 5;
  const rows = 5;
  const col = hash % cols;
  const row = Math.floor(hash / cols) % rows;
  // Cell centres mapped into the inset band.
  const usable = 100 - EDGE_INSET * 2;
  const x = EDGE_INSET + ((col + 0.5) / cols) * usable;
  const y = EDGE_INSET + ((row + 0.5) / rows) * usable;
  return { x, y };
}

/**
 * Project a seller to its `{ x, y }` percentage position. Uses the real
 * lat/lng when both are present (linear map onto the Bishkek box, with the
 * y-axis inverted because screen-y grows downward while latitude grows north);
 * otherwise falls back to the hashed grid.
 */
export function projectSeller(seller: SellerSummary): { x: number; y: number } {
  const lat = seller.lat == null ? null : parseDecimal(seller.lat);
  const lng = seller.lng == null ? null : parseDecimal(seller.lng);

  if (lat === null || lng === null) {
    return hashedGridPosition(seller.id);
  }

  const { latMin, latMax, lngMin, lngMax } = BISHKEK_BOUNDS;
  const xFraction = (lng - lngMin) / (lngMax - lngMin);
  // North (high lat) → top of the map (low y), so invert.
  const yFraction = (latMax - lat) / (latMax - latMin);

  const x = clampPct(EDGE_INSET + xFraction * (100 - EDGE_INSET * 2));
  const y = clampPct(EDGE_INSET + yFraction * (100 - EDGE_INSET * 2));
  return { x, y };
}

/**
 * Build the `MapPlaceholder` pins for a list of sellers. A seller with a large
 * catalogue (>200 positions) gets a star count badge (mirrors the prototype's
 * `positions > 200 ? "★"` convention; the placeholder badge is numeric, so we
 * use the rounded hundreds count instead — color is not the only signal and the
 * pin remains clickable either way).
 */
export function buildSellerPins(sellers: SellerSummary[]): SellerMapPin[] {
  return sellers.map((seller) => {
    const { x, y } = projectSeller(seller);
    const pin: SellerMapPin = { x, y, seller };
    if (seller.positions_count > 200) {
      pin.count = Math.round(seller.positions_count / 100);
    }
    return pin;
  });
}
